import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Card, Tab, TabGroup, TabList, Text, Title,
} from '@tremor/react';
import { formatMxn, formatHourLabel, localYMD } from '../lib/format';
import { fetchSalesPeriod } from '../api/salesPeriod';
import { t } from '../lib/i18n';
import type {
  SalesPeriod, SalesPeriodPayload, SalesToday,
} from '../api/types';
import SalesDeltaSummary from './SalesDeltaSummary';
import { makeSalesDeltaTooltip } from './SalesDeltaTooltip';

interface Props {
  /** Today's hourly series from the cron — used by the "Día" tab so the
   *  existing sub-second path keeps working. W/M/Y are lazy-fetched from
   *  the daemon on tab click. */
  today: SalesToday;
  /** When set, the operator filtered to a single store; pass it through
   *  to the daemon so the chart matches the lists below. */
  sid: number | null;
  /** Drill-down: a Día/Semana/Mes bar opens HourDrilldownModal for that
   *  date; a Año bar navigates to /sales filtered to that month. */
  onSelectHour?: (hour: string, date: string) => void;
}

type Tab = 'day' | 'week' | 'month' | 'year';
const TABS: { key: Tab; label: string }[] = [
  { key: 'day',   label: 'Día' },
  { key: 'week',  label: 'Semana' },
  { key: 'month', label: 'Mes' },
  { key: 'year',  label: 'Año' },
];

export default function SalesPeriodChart({ today, sid, onSelectHour }: Props) {
  const [tab, setTab] = useState<Tab>('day');
  // Cache per-(tab, sid) keeps a re-click cheap — the Go side also caches,
  // but skipping the network entirely makes tab switching feel native.
  const [cache, setCache] = useState<Record<string, SalesPeriodPayload>>({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cacheKey = tab + ':' + (sid ?? 'all');

  useEffect(() => {
    if (tab === 'day') return;
    if (cache[cacheKey]) return;
    let alive = true;
    setLoading(true);
    setErr(null);
    fetchSalesPeriod(tab as SalesPeriod, sid)
      .then((p) => { if (alive) setCache((prev) => ({ ...prev, [cacheKey]: p })); })
      .catch((e: Error) => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tab, cacheKey, cache, sid]);

  return (
    <Card>
      <div className="flex items-baseline gap-3">
        <Title>{t.charts.salesPeriodTitle}</Title>
      </div>
      <TabGroup
        index={TABS.findIndex((x) => x.key === tab)}
        onIndexChange={(i) => setTab(TABS[i].key)}
        className="mt-2"
      >
        <TabList variant="solid">
          {TABS.map((x) => <Tab key={x.key}>{x.label}</Tab>)}
        </TabList>
      </TabGroup>

      {tab === 'day' && (
        <DayChart today={today} onSelectHour={onSelectHour} />
      )}
      {tab !== 'day' && loading && (
        <div className="mt-4 animate-pulse space-y-2">
          <div className="h-4 w-48 bg-tremor-background-muted rounded" />
          <div className="h-64 bg-tremor-background-muted rounded" />
        </div>
      )}
      {tab !== 'day' && err && (
        <Text className="mt-4 text-red-700 dark:text-red-400">{err}</Text>
      )}
      {tab !== 'day' && !loading && !err && cache[cacheKey] && (
        <PeriodBars
          payload={cache[cacheKey]}
          onSelectBucket={onSelectHour}
        />
      )}
    </Card>
  );
}

// DayChart is the existing 24-hour bar chart (today vs same day last week).
// Kept inline to preserve the cron-cached zero-latency switch and to keep
// the bar-click → HourDrilldown plumbing tight.
function DayChart({
  today, onSelectHour,
}: { today: SalesToday; onSelectHour?: (hour: string, date: string) => void }) {
  const chartData = today.hours.map((p) => ({
    hour: formatHourLabel(p.hour),
    _hourKey: p.hour,
    [t.series.today]: p.today_mxn,
    [t.series.lastWeek]: p.last_week_mxn,
  }));

  // To-date totals: sum hours up to (and including) the current hour so we
  // compare partial-today vs partial-prior-week-day apples-to-apples.
  // Otherwise at 10am the summary would look catastrophic — today's 14
  // empty hours dragging the side-by-side comparison down.
  const totals = useMemo(() => {
    const nowHour = new Date().getHours();
    let cur = 0, pri = 0;
    for (const p of today.hours) {
      const h = parseInt(p.hour, 10);
      if (!Number.isFinite(h) || h > nowHour) continue;
      cur += p.today_mxn;
      pri += p.last_week_mxn;
    }
    return { cur, pri };
  }, [today]);

  const Tooltip = useMemo(
    () => makeSalesDeltaTooltip({ currentLabel: t.series.today, priorLabel: t.series.lastWeek }),
    [],
  );

  return (
    <>
      <SalesDeltaSummary
        currentLabel={t.series.today}
        priorLabel={t.series.lastWeek}
        currentMxn={totals.cur}
        priorMxn={totals.pri}
        partial
      />
      <Text className="mt-2 text-tremor-content text-xs">
        {t.charts.salesTodaySubtitle}
        {onSelectHour ? ` · ${t.charts.clickHourHint}` : ''}
      </Text>
      <BarChart
        className="mt-4 h-72"
        data={chartData}
        index="hour"
        categories={[t.series.today, t.series.lastWeek]}
        colors={['blue', 'gray']}
        valueFormatter={formatMxn}
        yAxisWidth={72}
        customTooltip={Tooltip}
        onValueChange={(v) => {
          if (!onSelectHour || !v) return;
          const payload = v as unknown as { _hourKey?: string; categoryClicked?: string };
          if (!payload._hourKey) return;
          const now = new Date();
          const date =
            payload.categoryClicked === t.series.lastWeek
              ? localYMD(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000))
              : localYMD(now);
          onSelectHour(payload._hourKey, date);
        }}
      />
    </>
  );
}

// PeriodBars renders the W/M/Y grouped-bar view: one set per bucket, with
// current_mxn (blue) + prior_mxn (gray) categories. The bucket label is
// the X axis; bars carry a back-pointer to a YYYY-MM-DD so click-throughs
// can resolve which date to drill.
function PeriodBars({
  payload, onSelectBucket,
}: {
  payload: SalesPeriodPayload;
  onSelectBucket?: (hour: string, date: string) => void;
}) {
  const navigate = useNavigate();
  const data = payload.points.map((p) => ({
    bucket: p.label,
    [payload.current_label]: p.current_mxn,
    [payload.prior_label]:   p.prior_mxn,
    // Synthesize a resolvable date back to the bucket so drill works.
    _currentDate: dateForBucket(payload.period, p.label, 'current'),
    _priorDate:   dateForBucket(payload.period, p.label, 'prior'),
  }));

  // To-date slice index for the prior series. Current is naturally to-date
  // because the daemon walks tkt.idate <= today, so its future buckets
  // are zero. Prior was the *full* historical period — slice the SPA side.
  // Indexes match the daemon's bucket order:
  //   week  → Mon=0..Sun=6
  //   month → 1..N (so cutoff = current day of month)
  //   year  → Jan=0..Dec=11
  const totals = useMemo(() => {
    const now = new Date();
    let lastBucket = payload.points.length - 1;
    if (payload.period === 'week') {
      // (jsDow + 6) % 7: Mon=0..Sun=6
      lastBucket = (now.getDay() + 6) % 7;
    } else if (payload.period === 'month') {
      lastBucket = now.getDate() - 1; // bucket "01" is index 0
    } else if (payload.period === 'year') {
      lastBucket = now.getMonth();
    }
    let cur = 0, pri = 0;
    payload.points.forEach((p, i) => {
      if (i > lastBucket) return;
      cur += p.current_mxn;
      pri += p.prior_mxn;
    });
    return { cur, pri };
  }, [payload]);

  const Tooltip = useMemo(
    () => makeSalesDeltaTooltip({
      currentLabel: payload.current_label,
      priorLabel: payload.prior_label,
    }),
    [payload.current_label, payload.prior_label],
  );

  return (
    <>
      <SalesDeltaSummary
        currentLabel={payload.current_label}
        priorLabel={payload.prior_label}
        currentMxn={totals.cur}
        priorMxn={totals.pri}
        partial
      />
      <BarChart
        className="mt-4 h-72"
        data={data}
        index="bucket"
        categories={[payload.current_label, payload.prior_label]}
        colors={['blue', 'gray']}
        valueFormatter={formatMxn}
        yAxisWidth={72}
        customTooltip={Tooltip}
        onValueChange={(v) => {
          if (!v) return;
          const p = v as unknown as { _currentDate?: string; _priorDate?: string; categoryClicked?: string; bucket?: string };
          const isPrior = p.categoryClicked === payload.prior_label;
          const dateISO = isPrior ? p._priorDate : p._currentDate;
          if (!dateISO) return;
          if (payload.period === 'year') {
            // Año bar = month bucket. Navigate to /sales pre-filtered to that
            // month's range — too wide for the per-hour drill modal.
            const start = dateISO.slice(0, 7) + '-01';
            const monthEnd = lastDayOfMonth(dateISO);
            navigate(`/sales?from=${start}&to=${monthEnd}`);
          } else if (onSelectBucket) {
            // Semana/Mes bar = day bucket. Reuse the per-hour drill modal
            // since it already loads /api/sales for a date.
            onSelectBucket('00', dateISO);
          }
        }}
      />
    </>
  );
}

// dateForBucket maps (period, bucket-label) → ISO date the drill should
// open. Labels are server-emitted: weekday-strings ("Lun".."Dom") for
// week, day-of-month ("01".."31") for month, month-name ("Ene".."Dic")
// for year. Resolution uses today's clock as the anchor — same convention
// as the daemon proc.
const MONTH_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function dateForBucket(period: SalesPeriod, label: string, which: 'current' | 'prior'): string {
  const now = new Date();
  if (period === 'week') {
    // Monday-anchored week. WEEKDAY: Sun=0..Sat=6 in JS.
    const jsDow = now.getDay();             // 0=Sun..6=Sat
    const offsetFromMonday = (jsDow + 6) % 7; // Mon=0
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offsetFromMonday);
    const dayIdx = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].indexOf(label);
    if (dayIdx < 0) return '';
    const dt = new Date(monday);
    dt.setDate(monday.getDate() + dayIdx);
    if (which === 'prior') dt.setDate(dt.getDate() - 7);
    return localISO(dt);
  }
  if (period === 'month') {
    const d = parseInt(label, 10);
    if (!Number.isFinite(d)) return '';
    const year  = which === 'prior' ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth();
    return localISO(new Date(year, month, d));
  }
  // year
  const mIdx = MONTH_ES.indexOf(label);
  if (mIdx < 0) return '';
  const year = which === 'prior' ? now.getFullYear() - 1 : now.getFullYear();
  return localISO(new Date(year, mIdx, 1));
}

function localISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function lastDayOfMonth(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  // Day 0 of next month = last day of current.
  return localISO(new Date(y, m, 0));
}
